/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import { Box, IconButton } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';
import { RefObject, useCallback } from 'react';
import { useScrollPosition } from '../hooks/useScrollPosition';

export interface ScrollButtonProps {
  onClick: () => void;
  visible: boolean;
  direction: 'up' | 'down';
}

const buttonStyles = css`
  background: color-mix(in srgb, var(--mui-palette-background-default) 80%, transparent);

  color: var(--mui-palette-primary-main);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  width: 40px;
  height: 40px;

  &:hover {
    background: color-mix(in srgb, var(--mui-palette-background-default) 96%, transparent);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.25);
  }

  .MuiSvgIcon-root {
    font-size: 1.25rem;
  }
`;

const MotionIconButton = motion(IconButton);

export function ScrollButton({
  onClick,
  visible,
  direction,
  css,
}: ScrollButtonProps & { css?: SerializedStyles }) {
  const Icon = direction === 'up' ? NorthIcon : SouthIcon;
  const ariaLabel = direction === 'up' ? 'Scroll to top' : 'Scroll to bottom';

  return (
    <AnimatePresence>
      {visible && (
        <MotionIconButton
          css={[buttonStyles, css]}
          onClick={onClick}
          aria-label={ariaLabel}
          initial={{ opacity: 0, scale: 0.8, y: direction === 'up' ? -10 : 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: direction === 'up' ? -10 : 10 }}
          transition={{
            duration: 0.2,
            ease: 'easeInOut',
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Icon />
        </MotionIconButton>
      )}
    </AnimatePresence>
  );
}

const ContainerStyles = css`
  position: absolute;
  bottom: calc(5rem + env(safe-area-inset-bottom, 0px));
  z-index: 1005;
  left: 50%;
  transform: translateX(-50%);
  height: 0;
  max-height: 0;
  overflow: visible;
`;

export interface ScrollButtonsProps {
  containerRef: RefObject<HTMLDivElement | null>;
}

export function ScrollToBottom({ containerRef }: ScrollButtonsProps) {
  const { showScrollToBottom } = useScrollPosition(containerRef, {
    threshold: 200,
    throttleMs: 200,
  });

  const handleScrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [containerRef]);

  return (
    <Box css={ContainerStyles}>
      <ScrollButton onClick={handleScrollToBottom} visible={showScrollToBottom} direction={'down'} />
    </Box>
  );
}
